import { AttachInternals, Component, Element, h, Host, Prop } from '@stencil/core'

import { CssClassMap } from '../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/list?tab=designer).
 *
 * @slot default - Slot para os itens da lista.
 * @slot header - Slot para ações ou conteúdo do cabeçalho da lista.
 */
@Component({
  tag: 'br-list',
  styleUrl: 'list.scss',
  shadow: true,
  formAssociated: true,
})
export class List {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrListElement

  @AttachInternals() elementInternals: ElementInternals

  /**
   * Define o cabeçalho para a lista.
   */
  @Prop({ reflect: true }) readonly header: string = null

  /**
   * Indica se a lista será horizontal. Por padrão, a lista é vertical.
   */
  @Prop({ reflect: true }) readonly isHorizontal: boolean = false

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = `br-list-${listId++}`

  /**
   * Indica que o divider para o título da lista estará oculto.
   */
  @Prop({ reflect: true }) readonly hideHeaderDivider: boolean = false

  /**
   * Indica se a lista possui o comportamento de collapse.
   */
  @Prop({ reflect: true }) readonly collapse: boolean = false

  /**
   * Indica se a lista possui o comportamento de accordion.
   * O valor da propriedade define o grupo, ou seja, o accordion é aplicado a todas as listas que possuem o mesmo valor para esta propriedade.
   */
  @Prop({ reflect: true }) readonly accordion: string = null

  /**
   * Método do ciclo de vida do componente, que é chamado uma vez após o componente começar a ser renderizado no DOM.
   */
  componentDidLoad() {
    if (this.isHorizontal) {
      const slotItems = Array.from(this.el.querySelectorAll('br-item'))

      slotItems.forEach((item) => {
        const nextElement = item.nextElementSibling
        if (nextElement?.classList.contains('br-divider')) {
          const divider = nextElement as HTMLElement
          divider.style.borderWidth = '0 1px 0 0'
          divider.style.width = '1px'
        }
      })
    }

    // adicionar role="listitem" para os itens dentro do slot
    const items = Array.from(this.el.querySelectorAll('br-item'))
    items.forEach((item) => {
      item.setAttribute('role', 'listitem')
    })
  }

  /**
   * Método que retorna as classes CSS do componente
   * @returns Objeto com as classes CSS do componente
   */
  private getCssClassMap(): CssClassMap {
    return {
      'br-list': true,
      horizontal: this.isHorizontal,
    }
  }

  /**
   * Verifica se o slot possui conteúdo.
   * @param slotName Nome do slot a ser verificado.
   * @returns true se o slot possui conteúdo, false caso contrário.
   */
  private hasSlotContent(slotName: string): boolean {
    const directChildren = Array.from(this.el.querySelectorAll(`[slot="${slotName}"]`)).filter(
      (child) => child.parentElement === this.el
    )
    return Boolean(directChildren.length)
  }

  /**
   * Renderiza o cabeçalho da lista.
   * @returns JSX do cabeçalho da lista
   */

  private readonly renderHeader = () => {
    if (this.header?.length > 0 || this.hasSlotContent('header')) {
      return (
        <div
          class={!this.hasSlotContent('header') ? 'header' : null}
          slot={this.collapse || Boolean(this.accordion) ? 'trigger' : null}
        >
          <slot name="header">{!this.hasSlotContent('header') ? <div class="title">{this.header}</div> : null}</slot>
        </div>
      )
    }
    return null
  }

  /**
   * Renderiza o divider do cabeçalho.
   * @returns JSX do divider
   */
  private renderDivider() {
    if ((this.header || this.hasSlotContent('header')) && !this.hideHeaderDivider) {
      return <div class="br-divider"></div>
    }
    return null
  }

  /**
   * Renderiza o corpo da lista.
   * @returns JSX do corpo da lista.
   */
  private renderListBody() {
    return [this.renderHeader(), this.renderDivider(), <slot></slot>]
  }

  /**
   * Renderiza o componente.
   * @returns JSX do componente
   */
  render() {
    return (
      <Host role="list">
        <div class={this.getCssClassMap()} id={this.customId}>
          {Boolean(this.accordion) || this.collapse ? (
            <br-collapse accordion-group={this.accordion || null}>{this.renderListBody()}</br-collapse>
          ) : (
            this.renderListBody()
          )}
        </div>
      </Host>
    )
  }
}

let listId = 0
